import { define, html, router, store } from "hybrids";

import ArtistVideo from "~/stores/ArtistVideo";
import VideoDonation from "~/stores/VideoDonation";
import PaymentMethods from "~/stores/PaymentMethods";

import Payments from "./Payments";

interface VVideoDonateAmountView {
  video?: ArtistVideo;
  payments: PaymentMethods;
  donation: VideoDonation;
  tipOption: "5%" | "7.5%" | "10%" | "12.5%" | "custom";
}

function getTipValue(amount: number, tipOption: string) {
  return (parseFloat(tipOption.replace("%", "")) / 100) * amount;
}

function setTipOption(
  host: VVideoDonateAmountView,
  e: { target: HTMLInputElement }
) {
  const { amount } = host.donation;
  if (!amount) return;

  const value = (e.target as HTMLInputElement).value;
  const tip = value !== "custom" ? getTipValue(amount, value) : 0;

  store.set(host.donation, { tip });
}

function setAmountAndTip(host: VVideoDonateAmountView, e: Event) {
  const value = (e.target as HTMLInputElement).valueAsNumber;
  const { tip } = host.donation;

  store.set(host.donation, {
    amount: value,
    tip: host.tipOption !== "custom" ? getTipValue(value, host.tipOption) : tip,
  });
}

function submit(host: VVideoDonateAmountView, event: Event) {
  router.resolve(event, store.submit(host.donation, { video: host.video }));
}

export default define<VVideoDonateAmountView>({
  [router.connect]: { stack: [Payments] },
  tag: "v-video-donate-amount-view",
  video: store(ArtistVideo),
  payments: store(PaymentMethods, {
    id: ({ video }) => (store.ready(video) ? video?.owner?.id : undefined),
  }),
  donation: store(VideoDonation, { draft: true }),
  tipOption: "7.5%",
  content: ({ payments, tipOption, donation }) => html`
    <v-broadcast-nav-bar close>Donate</v-broadcast-nav-bar>

    ${payments &&
    store.ready(payments) &&
    html`
      <v-ui-flexbox gap="large">
        <section>
          <v-ui-text weight="semibold" ygutter="1">
            Enter amount (${payments.currency})
          </v-ui-text>
          <v-ui-input prefixText="${payments.currency}">
            <input
              name="amount"
              placeholder="Enter amount"
              type="number"
              min="0"
              value="${donation.amount || ""}"
              oninput="${setAmountAndTip}"
            />
          </v-ui-input>
        </section>
        <section>
          <v-ui-text weight="semibold" ygutter="1">Tip Viewcy</v-ui-text>
          <v-ui-text ygutter="1">
            Viewcy relies on generous patrons like you to keep streaming free.
          </v-ui-text>
          <v-ui-select onchange="${html.set("tipOption")}">
            <select value="${tipOption}" onchange="${setTipOption}">
              <option value="5%">add 5%</option>
              <option selected value="7.5%">add 7.5%</option>
              <option value="10%">add 10%</option>
              <option value="12.5%">add 12.5%</option>
              <option value="custom">custom</option>
            </select>
          </v-ui-select>
        </section>

        ${tipOption === "custom" &&
        html`
          <section>
            <v-ui-text weight="semibold" ygutter="1">
              Enter tip amount (${payments.currency})
            </v-ui-text>
            <v-ui-input prefixText="${payments.currency}">
              <input
                name="tip"
                placeholder="Enter tip"
                type="number"
                min="0"
                value="${donation.tip || ""}"
                oninput="${html.set(donation, "tip")}"
              />
            </v-ui-input>
          </section>
        `}

        <v-ui-button
          href="${router.url(Payments, { donation })}"
          onclick="${submit}"
          pending=${store.pending(donation)}
          disabled="${donation.disabled}"
        >
          Continue
        </v-ui-button>
      </v-ui-flexbox>
    `}
  `,
});
